import { useUnifiedAuth } from '@/contexts/UnifiedAuthContext';
import type { AdminUser } from '../services/adminService';

// Admin role hierarchy ordered highest → lowest (find returns the highest role the user holds)
const ADMIN_ROLES: AdminUser['role'][] = ['super_admin', 'finance', 'operations', 'viewer'];

interface UseAdminAuthReturn {
  adminUser: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  signInWithGoogle: (credential: string) => Promise<{ success: boolean; message: string }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

export const useAdminAuth = (): UseAdminAuthReturn => {
  const { user, loading, login, logout, refreshProfile } = useUnifiedAuth();

  const adminUser: AdminUser | null = user
    ? {
        id: user.id,
        email: user.email ?? '',
        fullName: user.displayName ?? user.email ?? '',
        role: ADMIN_ROLES.find(r => user.roles.includes(r)) ?? 'viewer',
      }
    : null;

  const signIn = async (email: string, password: string) => {
    try {
      await login(email, password, 'password');
      return { success: true, message: 'Sesión iniciada' };
    } catch (e) {
      return { success: false, message: e instanceof Error ? e.message : 'Error al iniciar sesión' };
    }
  };

  // Google sign-in is handled by the unified login page (/app/login)
  const signInWithGoogle = async (_credential: string) => {
    return { success: false, message: 'Usa /app/login para iniciar sesión con Google' };
  };

  const signOut = async () => { logout(); };

  const refreshSession = async () => { await refreshProfile(); };

  return {
    adminUser,
    isLoading: loading,
    isAuthenticated: !!adminUser,
    signIn,
    signInWithGoogle,
    signOut,
    refreshSession,
  };
};

export default useAdminAuth;
